import { useState, useEffect, useCallback, useMemo } from "react";
import { BookingPaymentDialog } from "@/components/BookingPaymentPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Gift, Loader2, RefreshCw, Plus, ShieldCheck, Copy, MessageCircle, Mail,
  Ban, Repeat, CheckCircle2, Download, ClipboardCopy, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import PremiumVoucherCard from "@/components/PremiumVoucherCard";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_OPTIONS = [
  "NEW_REQUEST", "CONTACTED", "PAYMENT_PENDING", "PAYMENT_RECEIVED",
  "ACTIVE", "REDEEMED", "CANCELLED", "EXPIRED",
];

const stageBadge = (s) => {
  const c = {
    NEW_REQUEST:      "bg-sky-500/20 text-sky-500 border-sky-500/30",
    CONTACTED:        "bg-indigo-500/20 text-indigo-500 border-indigo-500/30",
    PAYMENT_PENDING:  "bg-amber-500/20 text-amber-500 border-amber-500/30",
    PAYMENT_RECEIVED: "bg-teal-500/20 text-teal-500 border-teal-500/30",
    ACTIVE:           "bg-emerald-500/20 text-emerald-500 border-emerald-500/30",
    REDEEMED:         "bg-red-500/20 text-red-500 border-red-500/30",
    CANCELLED:        "bg-slate-500/20 text-slate-500 border-slate-500/30",
    EXPIRED:          "bg-orange-500/20 text-orange-500 border-orange-500/30",
  };
  return c[s] || "bg-gray-500/20 text-gray-400 border-gray-500/30";
};

// Currency helpers — the display currency ALWAYS derives from the voucher's
// ``currency`` field, which the backend resolves from the centre's country
// (India→INR, Australia→AUD, USA→USD, …) via ``utils/currency.py``. This
// helper renders the symbol; never hardcode "INR" in labels.
const CURRENCY_SYMBOLS = {
  INR: "\u20B9",   // ₹
  AUD: "A$",
  USD: "$",
  GBP: "\u00A3",   // £
  EUR: "\u20AC",   // €
  JPY: "\u00A5",   // ¥
  SGD: "S$",
  NZD: "NZ$",
  CAD: "C$",
};

const currencySymbol = (code) => CURRENCY_SYMBOLS[(code || "INR").toUpperCase()]
                                 || `${(code || "INR").toUpperCase()} `;

const money = (v, cur) => {
  const sym = currencySymbol(cur);
  const n = Number(v || 0).toLocaleString(undefined,
                     { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // ₹ / A$ / $ hug the digits; 3-letter fallbacks get a space (e.g. "SGD 100").
  return sym.endsWith(" ") ? `${sym}${n}` : `${sym}${n}`;
};

// Amount presets per currency — Australia gets AUD tiers, India gets INR.
const AMOUNT_PRESETS = {
  INR: [500, 1000, 1500, 2000, 5000],
  AUD: [50, 100, 150, 200, 500],
  USD: [25, 50, 100, 200, 500],
  GBP: [25, 50, 100, 200, 500],
  EUR: [25, 50, 100, 200, 500],
  JPY: [1000, 2500, 5000, 10000, 20000],
  SGD: [50, 100, 150, 200, 500],
  NZD: [50, 100, 150, 200, 500],
  CAD: [50, 100, 150, 200, 500],
};

const presetsFor = (cur) => AMOUNT_PRESETS[(cur || "INR").toUpperCase()] || AMOUNT_PRESETS.INR;

async function api(path, token, body) {
  const res = await fetch(`${API}/api/gift-voucher${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Request failed");
  return data;
}


export default function GiftVoucherManagement({ session, centers = [] }) {
  const token = session?.token;
  const isAdminLike = session?.is_super_admin || session?.is_admin;
  const userCenter = session?.center || "";

  const [tab, setTab] = useState("dashboard");
  const [centerFilter, setCenterFilter] = useState(isAdminLike ? "all" : userCenter);
  const [kpis, setKpis] = useState(null);
  const [requests, setRequests] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [stageDlg, setStageDlg] = useState(null);    // {row, stage}
  const [paymentDlg, setPaymentDlg] = useState(null);
  const [voucherDlg, setVoucherDlg] = useState(null);
  const [redeemDlg, setRedeemDlg] = useState(null);
  const [messageDlg, setMessageDlg] = useState(null);
  const [cancelDlg, setCancelDlg] = useState(null);
  const [reissueDlg, setReissueDlg] = useState(null);
  const [auditDlg, setAuditDlg] = useState(null);
  const [payReq, setPayReq] = useState(null);

  const centerParam = () => (centerFilter === "all" ? null : centerFilter);

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [d, r, v] = await Promise.all([
        api("/dashboard", token, { center: centerParam() }),
        api("/requests/list", token, {
          center: centerParam(),
          stage: stageFilter === "all" ? null : stageFilter,
          search: search || null,
        }),
        api("/vouchers/list", token, {
          center: centerParam(),
          status: statusFilter === "all" ? null : statusFilter,
          search: search || null,
        }),
      ]);
      setKpis(d);
      setRequests(r.requests || []);
      setVouchers(v.vouchers || []);
    } catch (e) {
      toast.error(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token, centerFilter, stageFilter, statusFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  const canCancelReissue = isAdminLike;

  return (
    <div className="space-y-4" data-testid="gift-voucher-management">
      <BookingPaymentDialog open={!!payReq} onClose={() => { setPayReq(null); refresh(); }} session={session} bookingType="GIFT_VOUCHER" bookingId={payReq?.request_id} />
      {/* Header + filters */}
      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          {isAdminLike && (
            <div className="w-56">
              <Label className="text-xs">Center</Label>
              <Select value={centerFilter} onValueChange={setCenterFilter}>
                <SelectTrigger className="h-9" data-testid="gv-center-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Centers</SelectItem>
                  {centers.map((c) => (
                    <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1">
            <Label className="text-xs">Search (name, mobile, voucher #, request id)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Type at least a few characters…"
              className="h-9" data-testid="gv-search" />
          </div>
          <Button variant="outline" onClick={refresh} disabled={loading}
            data-testid="gv-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="gv-new-request">
            <Plus className="w-4 h-4 mr-1" /> New Request
          </Button>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="gv-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="requests" data-testid="gv-tab-requests">Requests ({requests.length})</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="gv-tab-vouchers">Vouchers ({vouchers.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="gv-tab-reports">Reports</TabsTrigger>
          {session?.is_super_admin && (
            <TabsTrigger value="settings" data-testid="gv-tab-settings">Settings</TabsTrigger>
          )}
        </TabsList>

        {/* ── Dashboard ── */}
        <TabsContent value="dashboard" className="space-y-3">
          {!kpis ? (
            <div className="text-center py-10 text-muted-foreground">Loading dashboard…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <KpiCard tone="sky"     label="New Requests"      value={kpis.new_requests}     testid="kpi-new" />
              <KpiCard tone="indigo"  label="Contact Pending"   value={kpis.contact_pending}  testid="kpi-contact" />
              <KpiCard tone="amber"   label="Payment Pending"   value={kpis.payment_pending}  testid="kpi-payment-pending" />
              <KpiCard tone="teal"    label="Ready to Generate" value={kpis.ready_to_generate} testid="kpi-ready" />
              <KpiCard tone="emerald" label="Active Vouchers"   value={kpis.active_vouchers}  testid="kpi-active" />
              <KpiCard tone="red"     label="Redeemed"          value={kpis.redeemed_vouchers} testid="kpi-redeemed" />
              <KpiCard tone="slate"   label="Cancelled"         value={kpis.cancelled}         testid="kpi-cancelled" />
              <KpiCard tone="orange"  label="Expired"           value={kpis.expired}           testid="kpi-expired" />
              <KpiCard tone="fuchsia" label="Voucher Sales"     value={money(kpis.total_voucher_sales)} big testid="kpi-sales" />
              <KpiCard tone="rose"    label="Outstanding Liability" value={money(kpis.outstanding_liability)} big testid="kpi-outstanding" />
            </div>
          )}
        </TabsContent>

        {/* ── Requests ── */}
        <TabsContent value="requests" className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Filter by stage:</Label>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="h-8 w-52" data-testid="gv-stage-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request #</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Center</TableHead>
                    <TableHead>Purchaser</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Occasion</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No requests</TableCell></TableRow>
                  )}
                  {requests.map((r) => (
                    <TableRow key={r.request_id} data-testid={`gv-req-row-${r.request_id}`}>
                      <TableCell className="font-mono text-xs">{r.request_id}</TableCell>
                      <TableCell className="text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</TableCell>
                      <TableCell>{r.center}</TableCell>
                      <TableCell>
                        <div>{r.purchaser_name}</div>
                        <div className="text-xs text-muted-foreground">{r.purchaser_mobile}</div>
                      </TableCell>
                      <TableCell>
                        <div>{r.recipient_name}</div>
                        <div className="text-xs text-muted-foreground">{r.recipient_mobile}</div>
                      </TableCell>
                      <TableCell className="font-semibold">{money(r.voucher_amount, r.currency)}</TableCell>
                      <TableCell>{r.occasion || "—"}</TableCell>
                      <TableCell>
                        <Badge className={stageBadge(r.stage)} data-testid={`gv-req-stage-${r.request_id}`}>
                          {r.stage.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {["NEW_REQUEST", "CONTACTED"].includes(r.stage) && (
                          <Button size="sm" variant="outline" className="mr-1 h-7 text-xs"
                            onClick={() => setStageDlg({ row: r, stage: r.stage === "NEW_REQUEST" ? "CONTACTED" : "PAYMENT_PENDING" })}
                            data-testid={`gv-req-advance-${r.request_id}`}>
                            → {r.stage === "NEW_REQUEST" ? "Contacted" : "Payment Pending"}
                          </Button>
                        )}
                        {["NEW_REQUEST", "CONTACTED", "PAYMENT_PENDING", "PAYMENT_RECEIVED"].includes(r.stage) && !r.voucher_number && (
                          <button type="button" className="bi-pill !py-1 !px-2 !text-xs mr-1"
                            onClick={() => setPayReq(r)}
                            data-testid={`gv-req-payment-${r.request_id}`}>
                            {r.stage === "PAYMENT_RECEIVED" ? "View Payment" : r.stage === "PAYMENT_PENDING" ? "Verify Payment" : "Share Payment Link"}
                          </button>
                        )}
                        {r.stage === "PAYMENT_RECEIVED" && !r.voucher_number && (
                          <Button size="sm" className="mr-1 h-7 text-xs bg-emerald-700 hover:bg-emerald-800"
                            onClick={async () => {
                              try {
                                const res = await api("/generate", token, { request_id: r.request_id });
                                toast.success(`Voucher ${res.voucher.voucher_number} generated`);
                                setVoucherDlg(res.voucher);
                                refresh();
                              } catch (e) { toast.error(e.message); }
                            }}
                            data-testid={`gv-req-generate-${r.request_id}`}>
                            Generate Voucher
                          </Button>
                        )}
                        {r.voucher_number && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={async () => {
                              try {
                                const list = await api("/vouchers/list", token, { search: r.voucher_number });
                                const found = (list.vouchers || []).find((x) => x.voucher_number === r.voucher_number);
                                if (found) setVoucherDlg(found);
                              } catch (e) { toast.error(e.message); }
                            }}
                            data-testid={`gv-req-view-voucher-${r.request_id}`}>
                            View Voucher
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAuditDlg(r.request_id)}
                          title="Audit trail" data-testid={`gv-req-audit-${r.request_id}`}>
                          <ScrollText className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Vouchers ── */}
        <TabsContent value="vouchers" className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs">Filter by status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-40" data-testid="gv-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="REDEEMED">Redeemed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Voucher #</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Center</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vouchers.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No vouchers</TableCell></TableRow>
                  )}
                  {vouchers.map((v) => (
                    <TableRow key={v.voucher_number} data-testid={`gv-v-row-${v.voucher_number}`}>
                      <TableCell className="font-mono text-xs">{v.voucher_number}</TableCell>
                      <TableCell className="text-xs">{v.issued_at?.slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">{v.expires_at?.slice(0, 10)}</TableCell>
                      <TableCell>{v.center}</TableCell>
                      <TableCell>
                        <div>{v.recipient_name}</div>
                        <div className="text-xs text-muted-foreground">{v.recipient_mobile}</div>
                      </TableCell>
                      <TableCell className="font-semibold">{money(v.voucher_amount, v.currency)}</TableCell>
                      <TableCell>
                        <Badge className={stageBadge(v.status)} data-testid={`gv-v-status-${v.voucher_number}`}>
                          {v.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => setVoucherDlg(v)}
                          data-testid={`gv-v-view-${v.voucher_number}`}>View</Button>
                        {v.status === "ACTIVE" && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-500"
                            onClick={async () => {
                              try {
                                const msg = await api("/compose-message", token, { voucher_number: v.voucher_number });
                                setMessageDlg({ voucher: v, token, ...msg });
                              } catch (e) { toast.error(e.message); }
                            }}
                            data-testid={`gv-v-send-${v.voucher_number}`}>
                            <MessageCircle className="w-3 h-3 mr-1" /> Send
                          </Button>
                        )}
                        {v.status === "ACTIVE" && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-500"
                            onClick={() => setRedeemDlg(v)}
                            data-testid={`gv-v-redeem-${v.voucher_number}`}>
                            <ShieldCheck className="w-3 h-3 mr-1" /> Redeem
                          </Button>
                        )}
                        {canCancelReissue && v.status === "ACTIVE" && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500"
                            onClick={() => setCancelDlg(v)}
                            data-testid={`gv-v-cancel-${v.voucher_number}`}>
                            <Ban className="w-3 h-3" />
                          </Button>
                        )}
                        {canCancelReissue && ["CANCELLED", "EXPIRED"].includes(v.status) && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs"
                            onClick={() => setReissueDlg(v)}
                            data-testid={`gv-v-reissue-${v.voucher_number}`}>
                            <Repeat className="w-3 h-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 text-xs"
                          onClick={() => setAuditDlg(v.voucher_number)}
                          data-testid={`gv-v-audit-${v.voucher_number}`}>
                          <ScrollText className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Reports ── */}
        <TabsContent value="reports">
          <ReportsPanel token={token} center={centerParam()} />
        </TabsContent>

        {/* ── Settings (Super Admin only) ── */}
        {session?.is_super_admin && (
          <TabsContent value="settings">
            <VoucherTemplatesPanel token={token} />
          </TabsContent>
        )}
      </Tabs>

      {/* Create Request */}
      <CreateRequestDialog open={createOpen} onOpenChange={setCreateOpen}
        token={token} defaultCenter={centerFilter === "all" ? "" : centerFilter}
        centers={centers} onDone={refresh} />

      {/* Stage advance */}
      {stageDlg && (
        <StageAdvanceDialog dlg={stageDlg} onClose={() => setStageDlg(null)}
          token={token} onDone={refresh} />
      )}

      {/* Payment */}
      {paymentDlg && (
        <PaymentDialog req={paymentDlg} onClose={() => setPaymentDlg(null)}
          token={token} onDone={refresh} />
      )}

      {/* Voucher detail */}
      {voucherDlg && (
        <VoucherDetailDialog v={voucherDlg} onClose={() => setVoucherDlg(null)}
          token={token} onSendClick={async () => {
            try {
              const msg = await api("/compose-message", token, { voucher_number: voucherDlg.voucher_number });
              setMessageDlg({ voucher: voucherDlg, token, ...msg });
            } catch (e) { toast.error(e.message); }
          }} />
      )}

      {/* Message compose */}
      {messageDlg && (
        <MessageDialog dlg={messageDlg} onClose={() => setMessageDlg(null)} />
      )}

      {/* Redeem */}
      {redeemDlg && (
        <RedeemDialog v={redeemDlg} onClose={() => setRedeemDlg(null)}
          token={token} defaultCenter={userCenter} onDone={refresh} />
      )}

      {/* Cancel */}
      {cancelDlg && (
        <ReasonDialog title={`Cancel voucher ${cancelDlg.voucher_number}?`}
          confirmLabel="Cancel Voucher" testidPrefix="gv-cancel"
          onClose={() => setCancelDlg(null)}
          onConfirm={async (reason) => {
            try {
              await api("/cancel", token, { voucher_number: cancelDlg.voucher_number, reason });
              toast.success("Voucher cancelled");
              setCancelDlg(null); refresh();
            } catch (e) { toast.error(e.message); }
          }} />
      )}

      {/* Reissue */}
      {reissueDlg && (
        <ReasonDialog title={`Reissue voucher ${reissueDlg.voucher_number}?`}
          confirmLabel="Reissue" testidPrefix="gv-reissue"
          onClose={() => setReissueDlg(null)}
          onConfirm={async (reason) => {
            try {
              const res = await api("/reissue", token, { voucher_number: reissueDlg.voucher_number, reason });
              toast.success(`New voucher ${res.voucher.voucher_number} issued`);
              setReissueDlg(null); refresh();
            } catch (e) { toast.error(e.message); }
          }} />
      )}

      {/* Audit */}
      {auditDlg && (
        <AuditDialog refId={auditDlg} onClose={() => setAuditDlg(null)} token={token} />
      )}
    </div>
  );
}


function KpiCard({ tone, label, value, big, testid }) {
  const toneMap = {
    sky:      "from-sky-500/20 to-sky-500/5 text-sky-500",
    indigo:   "from-indigo-500/20 to-indigo-500/5 text-indigo-500",
    amber:    "from-amber-500/20 to-amber-500/5 text-amber-500",
    teal:     "from-teal-500/20 to-teal-500/5 text-teal-500",
    emerald:  "from-emerald-500/20 to-emerald-500/5 text-emerald-500",
    red:      "from-red-500/20 to-red-500/5 text-red-500",
    slate:    "from-slate-500/20 to-slate-500/5 text-slate-500",
    orange:   "from-orange-500/20 to-orange-500/5 text-orange-500",
    fuchsia:  "from-fuchsia-500/20 to-fuchsia-500/5 text-fuchsia-500",
    rose:     "from-rose-500/20 to-rose-500/5 text-rose-500",
  }[tone] || "from-gray-500/20 to-gray-500/5 text-gray-400";
  return (
    <Card className={`bg-gradient-to-br ${toneMap} border-white/5`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
        <div className={`mt-1 font-bold ${big ? "text-2xl" : "text-3xl"}`}>{value}</div>
      </CardContent>
    </Card>
  );
}


function CreateRequestDialog({ open, onOpenChange, token, defaultCenter, centers, onDone }) {
  const [form, setForm] = useState({
    center: defaultCenter,
    purchaser_name: "", purchaser_mobile: "", purchaser_email: "",
    recipient_name: "", recipient_mobile: "", recipient_email: "",
    voucher_amount: 500, occasion: "", personal_message: "",
    preferred_delivery_date: "", internal_notes: "",
  });
  useEffect(() => { setForm((f) => ({ ...f, center: defaultCenter })); }, [defaultCenter, open]);
  const [busy, setBusy] = useState(false);

  // Currency lookup: derive from the selected centre's country/currency
  // fields in ``db.centers`` (single source of truth). The backend also
  // does this on ``/requests/create`` — the client-side lookup is purely
  // presentational (amount presets, ₹/A$ prefix in the input).
  const centerDoc = useMemo(
    () => (centers || []).find((c) => (c.code || c) === form.center) || {},
    [centers, form.center],
  );
  const centerCurrency = useMemo(() => {
    const explicit = (centerDoc.currency || "").toUpperCase();
    if (explicit && CURRENCY_SYMBOLS[explicit] !== undefined) return explicit;
    const country = (centerDoc.country || "").toLowerCase();
    const cc = {
      "india": "INR", "australia": "AUD", "usa": "USD", "united states": "USD",
      "uk": "GBP", "united kingdom": "GBP", "japan": "JPY", "singapore": "SGD",
      "new zealand": "NZD", "canada": "CAD",
    }[country];
    if (cc) return cc;
    if (centerDoc.is_india_center === true) return "INR";
    if (centerDoc.is_india_center === false) return "AUD";
    return "INR";
  }, [centerDoc]);

  // Reset amount to a sensible default whenever the currency band changes.
  useEffect(() => {
    setForm((f) => {
      const p = presetsFor(centerCurrency);
      // Only auto-adjust if the current amount is clearly out-of-band
      // (e.g. switching from INR 500 → AUD centre should snap to 50).
      if (!p.includes(f.voucher_amount)) {
        return { ...f, voucher_amount: p[0] };
      }
      return f;
    });
  }, [centerCurrency]);

  const submit = async () => {
    if (!form.center) { toast.error("Center is required"); return; }
    if (!form.purchaser_name || !form.recipient_name) { toast.error("Purchaser & Recipient names are required"); return; }
    if (!form.voucher_amount || form.voucher_amount <= 0) { toast.error("Voucher amount must be > 0"); return; }
    setBusy(true);
    try {
      await api("/requests/create", token, form);
      toast.success("Request created");
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const sym = currencySymbol(centerCurrency);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="gv-create-dialog">
        <DialogHeader><DialogTitle>New Gift Voucher Request</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Center *</Label>
            <Select value={form.center} onValueChange={(v) => setForm((f) => ({ ...f, center: v }))}>
              <SelectTrigger data-testid="gv-create-center"><SelectValue placeholder="Select center" /></SelectTrigger>
              <SelectContent>
                {centers.map((c) => <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground mt-1" data-testid="gv-create-currency-label">
              Currency: <span className="font-semibold">{sym} {centerCurrency}</span>
            </div>
          </div>
          <div>
            <Label>Voucher Amount * <span className="text-xs text-muted-foreground">({sym})</span></Label>
            <Input type="number" min="1" value={form.voucher_amount}
              onChange={(e) => setForm((f) => ({ ...f, voucher_amount: parseFloat(e.target.value) || 0 }))}
              data-testid="gv-create-amount" />
            <div className="flex flex-wrap gap-1 mt-2">
              {presetsFor(centerCurrency).map((amt) => (
                <Button key={amt} type="button" size="sm" variant="outline"
                  className={form.voucher_amount === amt ? "ring-2 ring-primary" : ""}
                  onClick={() => setForm((f) => ({ ...f, voucher_amount: amt }))}
                  data-testid={`gv-create-amount-preset-${amt}`}>
                  {sym}{amt.toLocaleString()}
                </Button>
              ))}
            </div>
          </div>
          <div><Label>Purchaser Name *</Label><Input value={form.purchaser_name}
            onChange={(e) => setForm((f) => ({ ...f, purchaser_name: e.target.value }))} data-testid="gv-create-purchaser-name" /></div>
          <div><Label>Purchaser Mobile *</Label><Input value={form.purchaser_mobile}
            onChange={(e) => setForm((f) => ({ ...f, purchaser_mobile: e.target.value }))} data-testid="gv-create-purchaser-mobile" /></div>
          <div><Label>Purchaser Email</Label><Input value={form.purchaser_email}
            onChange={(e) => setForm((f) => ({ ...f, purchaser_email: e.target.value }))} /></div>
          <div><Label>Recipient Name *</Label><Input value={form.recipient_name}
            onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))} data-testid="gv-create-recipient-name" /></div>
          <div><Label>Recipient Mobile *</Label><Input value={form.recipient_mobile}
            onChange={(e) => setForm((f) => ({ ...f, recipient_mobile: e.target.value }))} data-testid="gv-create-recipient-mobile" /></div>
          <div><Label>Recipient Email</Label><Input value={form.recipient_email}
            onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))} /></div>
          <div><Label>Occasion</Label><Input value={form.occasion} placeholder="Birthday, Anniversary…"
            onChange={(e) => setForm((f) => ({ ...f, occasion: e.target.value }))} /></div>
          <div><Label>Preferred Delivery Date</Label><Input type="date" value={form.preferred_delivery_date}
            onChange={(e) => setForm((f) => ({ ...f, preferred_delivery_date: e.target.value }))} /></div>
          <div className="col-span-2"><Label>Personal Message</Label>
            <Textarea rows={2} value={form.personal_message}
              onChange={(e) => setForm((f) => ({ ...f, personal_message: e.target.value }))}
              data-testid="gv-create-message" /></div>
          <div className="col-span-2"><Label>Internal Notes</Label>
            <Textarea rows={2} value={form.internal_notes}
              onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="gv-create-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StageAdvanceDialog({ dlg, onClose, token, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="gv-stage-dialog">
        <DialogHeader><DialogTitle>Advance {dlg.row.request_id} to {dlg.stage.replace(/_/g, " ")}</DialogTitle></DialogHeader>
        <Label>Note (optional)</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="gv-stage-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                await api("/requests/update-stage", token, {
                  request_id: dlg.row.request_id, new_stage: dlg.stage, note,
                });
                toast.success(`Advanced to ${dlg.stage.replace(/_/g, " ")}`);
                onClose(); onDone && onDone();
              } catch (e) { toast.error(e.message); }
              finally { setBusy(false); }
            }} data-testid="gv-stage-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PaymentDialog({ req, onClose, token, onDone }) {
  const [form, setForm] = useState({
    amount_received: req.voucher_amount,
    payment_date: new Date().toISOString().slice(0, 10),
    payment_mode: "UPI",
    txn_ref: "", proof_url: "", internal_notes: "",
  });
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="gv-payment-dialog">
        <DialogHeader><DialogTitle>Record Payment — {req.request_id}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Amount *</Label><Input type="number" value={form.amount_received}
            onChange={(e) => setForm((f) => ({ ...f, amount_received: parseFloat(e.target.value) || 0 }))}
            data-testid="gv-payment-amount" /></div>
          <div><Label>Date *</Label><Input type="date" value={form.payment_date}
            onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))} /></div>
          <div><Label>Mode *</Label>
            <Select value={form.payment_mode} onValueChange={(v) => setForm((f) => ({ ...f, payment_mode: v }))}>
              <SelectTrigger data-testid="gv-payment-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["UPI", "Cash", "Card", "Bank Transfer", "Cheque", "PayPal", "Other"].map((m) =>
                  <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Txn Ref</Label><Input value={form.txn_ref}
            onChange={(e) => setForm((f) => ({ ...f, txn_ref: e.target.value }))} /></div>
          <div className="col-span-2"><Label>Proof URL (image / receipt link)</Label>
            <Input value={form.proof_url} onChange={(e) => setForm((f) => ({ ...f, proof_url: e.target.value }))} /></div>
          <div className="col-span-2"><Label>Internal Notes</Label>
            <Textarea rows={2} value={form.internal_notes}
              onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={async () => {
              if (!form.amount_received || form.amount_received <= 0) { toast.error("Amount must be > 0"); return; }
              setBusy(true);
              try {
                await api("/requests/record-payment", token, {
                  request_id: req.request_id, ...form,
                });
                toast.success("Payment recorded — request ready to generate voucher");
                onClose(); onDone && onDone();
              } catch (e) { toast.error(e.message); }
              finally { setBusy(false); }
            }} data-testid="gv-payment-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VoucherDetailDialog({ v, onClose, onSendClick }) {
  // Always trust the backend's canonical verify_url (stamped in
  // /vouchers/list). Falling back to window.location.origin would
  // diverge from ``PUBLIC_APP_URL`` and mismatch the QR baked into
  // /card.png — the root cause of the "QR link mismatch" bug.
  const verifyUrl = useMemo(() => {
    if (v?.verify_url) return v.verify_url;
    if (!v?.voucher_number || !v?.sig) return "";
    const qs = new URLSearchParams({ v: v.voucher_number, s: v.sig });
    return `${window.location.origin}/verify-voucher?${qs.toString()}`;
  }, [v?.verify_url, v?.voucher_number, v?.sig]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="gv-voucher-dialog">
        <DialogHeader>
          <DialogTitle>Voucher {v.voucher_number}</DialogTitle>
        </DialogHeader>
        <PremiumVoucherCard v={v} verifyUrl={verifyUrl} />
        {v.redemption && (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-xs" data-testid="gv-voucher-redeemed-info">
            <strong>Redeemed:</strong> {v.redemption.redeemed_at?.slice(0, 16).replace("T", " ")}
            · Center: {v.redemption.redeemed_center}
            · Invoice: {v.redemption.invoice_number}
            · Bill: {money(v.redemption.bill_amount, v.currency)}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {v.status === "ACTIVE" && onSendClick && (
            <Button onClick={onSendClick} data-testid="gv-voucher-send-btn">
              <MessageCircle className="w-4 h-4 mr-1" /> Send / Compose
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MessageDialog({ dlg, onClose }) {
  // Backend always returns absolute URLs (uses PUBLIC_APP_URL or the request
  // base_url). The QR baked into the PNG, the verify link, the WhatsApp
  // message and the mailto link all resolve to the same URL — that is the
  // "one voucher = one URL" invariant.
  const [mode, setMode] = useState(dlg.mode || "premium");
  const [payload, setPayload] = useState({
    message: dlg.message, verify_url: dlg.verify_url,
    card_image_url: dlg.card_image_url,
    whatsapp_url: dlg.whatsapp_url, mailto_url: dlg.mailto_url,
  });
  const [busy, setBusy] = useState(false);

  // Session-authenticated preview: we fetch the PNG bytes via the admin
  // ``/card/authed`` endpoint (which does NOT require a matching HMAC
  // signature) so the preview never breaks when the pod's HMAC key has
  // drifted from what the historic ``sig`` was signed with. The resulting
  // blob URL is scoped to this dialog and revoked on unmount.
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [resigning, setResigning] = useState(false);

  const loadPreview = useCallback(async () => {
    setPreviewError(null);
    setPreviewBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift-voucher/card/authed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: dlg.token,
          voucher_number: dlg.voucher.voucher_number,
          fmt: "png",
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Render failed (HTTP ${r.status})`);
      }
      const blob = await r.blob();
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch (e) {
      setPreviewError(e.message || "Voucher image unavailable");
    }
  }, [dlg.token, dlg.voucher.voucher_number]);

  useEffect(() => { loadPreview(); }, [loadPreview]);
  useEffect(() => () => {
    // Revoke on unmount to prevent memory leaks
    setPreviewBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
  }, []);

  const reload = async (nextMode) => {
    setBusy(true);
    try {
      const p = await api("/compose-message", dlg.token, {
        voucher_number: dlg.voucher.voucher_number, mode: nextMode,
      });
      setPayload({
        message: p.message,
        verify_url: p.verify_url,
        card_image_url: p.card_image_url,
        whatsapp_url: p.whatsapp_url,
        mailto_url: p.mailto_url,
      });
      setMode(nextMode);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // Build a WhatsApp link that includes the card image URL first, so the
  // preview appears above the greeting. The recipient sees the card, taps,
  // and the verify link opens the full experience.
  const waMessage = useMemo(() => {
    if (mode === "text") return payload.message;
    return `${payload.card_image_url}\n\n${payload.message}`;
  }, [mode, payload]);
  const waRecipient = (dlg.voucher.recipient_mobile || dlg.voucher.purchaser_mobile || "")
    .replace(/\D/g, "");
  const waHref = `https://wa.me/${waRecipient}?text=${encodeURIComponent(waMessage)}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(payload.verify_url); toast.success("Secure link copied"); }
    catch { toast.error("Copy failed"); }
  };

  // Download PNG/PDF via the session-authenticated endpoint. Because we
  // pass the auth token in the body, the download works even if the
  // public HMAC signature would otherwise fail (avoids the classic
  // "Download failed" toast on drifted-sig vouchers).
  const download = async (kind) => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift-voucher/card/authed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: dlg.token,
          voucher_number: dlg.voucher.voucher_number,
          fmt: kind,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || `Download failed (HTTP ${r.status})`);
      }
      const blob = await r.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `Purnabramha-Gift-Voucher-${dlg.voucher.voucher_number}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
    } catch (e) { toast.error(e.message); }
  };

  // Regenerate the sig on every voucher with the current HMAC key, then
  // reload compose-message + preview. Only shown when the preview failed
  // because of a HMAC drift ("Invalid or tampered voucher token") — for
  // Super Admins.
  const regenerateFiles = async () => {
    setResigning(true);
    try {
      const r = await api("/settings/resign-vouchers", dlg.token, {});
      toast.success(`Resigned ${r.resigned}/${r.scanned} vouchers`);
      await reload(mode);
      await loadPreview();
    } catch (e) { toast.error(e.message); }
    finally { setResigning(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="gv-message-dialog">
        <DialogHeader>
          <DialogTitle>Preview & Send — {dlg.voucher.voucher_number}</DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="inline-flex rounded-full border border-white/10 p-1 self-start">
          <button
            className={`px-4 py-1.5 text-xs rounded-full transition ${mode === "premium" ? "bg-emerald-600 text-white" : "text-muted-foreground hover:bg-white/5"}`}
            onClick={() => reload("premium")} disabled={busy || mode === "premium"}
            data-testid="gv-msg-mode-premium">
            Send as Premium Voucher Card
          </button>
          <button
            className={`px-4 py-1.5 text-xs rounded-full transition ${mode === "text" ? "bg-emerald-600 text-white" : "text-muted-foreground hover:bg-white/5"}`}
            onClick={() => reload("text")} disabled={busy || mode === "text"}
            data-testid="gv-msg-mode-text">
            Send as Plain Text
          </button>
        </div>

        {/* Preview area */}
        {mode === "premium" ? (
          <>
            <div className="rounded-lg overflow-hidden border border-white/5 bg-slate-950/40 min-h-[200px] flex items-center justify-center">
              {previewError ? (
                <div className="p-6 text-center space-y-3" data-testid="gv-msg-preview-error">
                  <div className="text-sm font-semibold text-red-500">
                    Voucher image unavailable
                  </div>
                  <div className="text-xs text-muted-foreground max-w-md mx-auto break-words">
                    {previewError}
                  </div>
                  <div className="flex items-center justify-center gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={loadPreview}
                      data-testid="gv-msg-preview-retry">
                      Retry
                    </Button>
                    <Button size="sm" onClick={regenerateFiles} disabled={resigning}
                      data-testid="gv-msg-regenerate">
                      {resigning && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Regenerate Voucher Files
                    </Button>
                  </div>
                </div>
              ) : previewBlobUrl ? (
                <img
                  src={previewBlobUrl}
                  alt="Premium Gift Voucher"
                  className="w-full h-auto"
                  data-testid="gv-msg-card-preview"
                />
              ) : (
                <div className="text-xs text-muted-foreground p-6">Rendering voucher…</div>
              )}
            </div>
            <div className="text-xs text-muted-foreground italic">
              This is exactly what your recipient will see when they open the message.
            </div>
            <Textarea rows={4} value={payload.message} readOnly className="font-mono text-xs"
              data-testid="gv-message-textarea" />
          </>
        ) : (
          <Textarea rows={14} value={payload.message} readOnly className="font-mono text-xs"
            data-testid="gv-message-textarea" />
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 border-t pt-3">
          <a href={waHref} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-emerald-700"
            data-testid="gv-msg-whatsapp">
            <MessageCircle className="w-4 h-4 mr-1" />
            {mode === "premium" ? "Send Card via WhatsApp" : "Send Text via WhatsApp"}
          </a>
          <a href={payload.mailto_url}
            className="inline-flex items-center rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-blue-700"
            data-testid="gv-msg-email">
            <Mail className="w-4 h-4 mr-1" /> Open Email Client
          </a>
          {mode === "premium" && (
            <>
              <Button variant="outline" onClick={() => download("png")} data-testid="gv-msg-download-png">
                <Download className="w-4 h-4 mr-1" /> Download PNG
              </Button>
              <Button variant="outline" onClick={() => download("pdf")} data-testid="gv-msg-download-pdf">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
            </>
          )}
          <Button variant="outline" onClick={copyLink} data-testid="gv-msg-copy-link">
            <ClipboardCopy className="w-4 h-4 mr-1" /> Copy Secure Link
          </Button>
        </div>

        <div className="text-[11px] text-muted-foreground border-t pt-2">
          Secure link:{" "}
          <a href={payload.verify_url} target="_blank" rel="noopener noreferrer"
             className="text-blue-500 underline break-all">{payload.verify_url}</a>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function RedeemDialog({ v, onClose, token, defaultCenter, onDone }) {
  const [form, setForm] = useState({
    invoice_number: "",
    bill_amount: v.voucher_amount,
    center: defaultCenter || v.center,
    notes: "",
    confirmed: false,
  });
  const [busy, setBusy] = useState(false);

  const doRedeem = async () => {
    if (!form.invoice_number) { toast.error("Invoice # is required"); return; }
    if (!form.confirmed) { toast.error("Please confirm the one-time-use warning."); return; }
    setBusy(true);
    try {
      // We need the sig — fetch via /verify with the voucher's sig NOT stored on
      // this list-row (privacy). We call /audit? No, /vouchers/list includes sig
      // for admins only. To make redeem work everywhere, allow the frontend to
      // pass through the stored sig from the row — vouchers list DOES return sig.
      const res = await api("/redeem", token, {
        voucher_number: v.voucher_number,
        sig: v.sig,
        center: form.center,
        invoice_number: form.invoice_number,
        bill_amount: form.bill_amount,
        notes: form.notes,
      });
      toast.success(`Voucher ${v.voucher_number} redeemed`);
      onClose(); onDone && onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="gv-redeem-dialog">
        <DialogHeader><DialogTitle>Redeem Voucher {v.voucher_number}</DialogTitle></DialogHeader>
        <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <strong>Value:</strong> {money(v.voucher_amount, v.currency)}<br />
          <strong>Recipient:</strong> {v.recipient_name} ({v.recipient_mobile})<br />
          <strong>Expires:</strong> {v.expires_at?.slice(0, 10)}<br />
          <em>This voucher will now be permanently redeemed. Continue?</em>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Invoice # *</Label><Input value={form.invoice_number}
            onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
            data-testid="gv-redeem-invoice" /></div>
          <div><Label>Bill Amount</Label><Input type="number" value={form.bill_amount}
            onChange={(e) => setForm((f) => ({ ...f, bill_amount: parseFloat(e.target.value) || 0 }))}
            data-testid="gv-redeem-bill" /></div>
          <div className="col-span-2"><Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.confirmed}
              onChange={(e) => setForm((f) => ({ ...f, confirmed: e.target.checked }))}
              data-testid="gv-redeem-confirm-check" />
            I confirm the recipient is present and this voucher will be permanently redeemed.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>No</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800"
            disabled={busy} onClick={doRedeem} data-testid="gv-redeem-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Yes, Redeem
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReasonDialog({ title, confirmLabel, testidPrefix, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <Label>Reason *</Label>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid={`${testidPrefix}-reason`} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} onClick={async () => {
              if (!reason.trim()) { toast.error("Reason is required"); return; }
              setBusy(true); await onConfirm(reason); setBusy(false);
            }} data-testid={`${testidPrefix}-confirm`}>
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AuditDialog({ refId, onClose, token }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const d = await api("/audit", token, { ref_id: refId });
        setEvents(d.events || []);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, [refId, token]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="gv-audit-dialog">
        <DialogHeader><DialogTitle>Audit Trail — {refId}</DialogTitle></DialogHeader>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="max-h-96 overflow-y-auto text-xs">
            {events.length === 0 ? (
              <div className="text-muted-foreground p-4 text-center">No events</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead><TableHead>Action</TableHead>
                    <TableHead>Actor</TableHead><TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((e) => (
                    <TableRow key={e.event_id}>
                      <TableCell className="font-mono text-[10px]">{e.timestamp?.slice(0, 19).replace("T", " ")}</TableCell>
                      <TableCell>{e.action}</TableCell>
                      <TableCell>{e.actor_by}</TableCell>
                      <TableCell>{e.actor_role}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function ReportsPanel({ token, center }) {
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState(null);
  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await fetch(`${API}/api/gift-voucher/reports/excel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center, kind, date_from: range.from, date_to: range.to }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gift-vouchers-${kind}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Voucher Reports (Excel)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><Label>From</Label><Input type="date" value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            data-testid="gv-report-from" /></div>
          <div><Label>To</Label><Input type="date" value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            data-testid="gv-report-to" /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { k: "sales_summary", label: "Voucher Sales Summary" },
            { k: "redeemed",      label: "Redeemed Vouchers" },
            { k: "cancelled",     label: "Cancelled Vouchers" },
            { k: "outstanding",   label: "Outstanding Liability" },
          ].map((r) => (
            <Button key={r.k} variant="outline" onClick={() => download(r.k)} disabled={busy === r.k}
              data-testid={`gv-report-${r.k}`}>
              {busy === r.k ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              {r.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


/* -------------------- Voucher Design Templates (Super Admin) -------------------- */
const EMPTY_TEMPLATE = {
  template_id: null,
  name: "",
  currency: "INR",
  amount: 0,
  is_default: false,
  bg_color_1: "#5a2a13",
  bg_color_2: "#3a1a08",
  bg_color_3: "#2a1006",
  accent_color: "#e6c069",
  accent2_color: "#f5deac",
  ink_color: "#f4d47c",
  ink_soft_color: "#c9a56b",
  stroke_color: "#8a5a24",
  background_image_base64: null,
};

function VoucherTemplatesPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // template row or EMPTY_TEMPLATE

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/settings/templates/list", token, {});
      setRows(r.templates || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api("/settings/templates/delete", token, { template_id: t.template_id });
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="gv-templates-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Voucher Design Templates</CardTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Configure color palettes mapped to (currency, amount) tiers. The renderer picks the exact
            match first, otherwise falls back to the default template.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline"
            onClick={async () => {
              try {
                const r = await api("/settings/render-health", token, {});
                const missing = Object.entries(r.fonts || {})
                  .filter(([, v]) => !v.exists).map(([k]) => k);
                if (!r.render?.ok) {
                  toast.error(`Render self-test FAILED: ${r.render?.error || "unknown"}`);
                } else if (missing.length) {
                  toast.warning(`Renderer OK — but ${missing.length} font(s) missing (fallback active): ${missing.join(", ")}`);
                } else {
                  toast.success(`Renderer healthy — logo ${r.logo.size_kb} KB, self-test ${r.render.png_kb} KB`);
                }
                console.info("Voucher render health:", r);
              } catch (e) { toast.error(e.message); }
            }}
            data-testid="gv-templates-render-health">
            Check Render Health
          </Button>
          <Button variant="outline"
            onClick={async () => {
              if (!window.confirm("Rewrite currency on every historic voucher & request to match the centre's current country/currency? Safe to re-run — only mis-mapped rows are touched.")) return;
              try {
                const r = await api("/settings/backfill-currency", token, {});
                toast.success(`Backfilled — requests ${r.requests.fixed}/${r.requests.scanned}, vouchers ${r.vouchers.fixed}/${r.vouchers.scanned}`);
                load();
              } catch (e) { toast.error(e.message); }
            }}
            data-testid="gv-templates-backfill-currency">
            Fix Historic Currency
          </Button>
          <Button variant="outline"
            onClick={async () => {
              if (!window.confirm("Re-sign every voucher with the current HMAC key? This heals vouchers that broke after a pod/secret restart. Existing voucher numbers and QR content are preserved — only the security signature changes.")) return;
              try {
                const r = await api("/settings/resign-vouchers", token, {});
                toast.success(`Resigned ${r.resigned}/${r.scanned} vouchers`);
              } catch (e) { toast.error(e.message); }
            }}
            data-testid="gv-templates-resign">
            Resign Vouchers
          </Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="gv-templates-refresh">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
          <Button onClick={() => setEditing({ ...EMPTY_TEMPLATE })} data-testid="gv-templates-new">
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Preview</TableHead>
              <TableHead>Default</TableHead>
              <TableHead>Custom BG</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.template_id} data-testid={`gv-template-row-${t.template_id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{t.currency}</TableCell>
                <TableCell className="text-right">{t.amount || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {[t.bg_color_1, t.bg_color_2, t.bg_color_3, t.accent_color, t.ink_color].map((c, i) => (
                      <span key={i}
                        className="inline-block w-5 h-5 rounded border border-white/20"
                        style={{ background: c }}
                        title={c}
                      />
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {t.is_default ? <Badge variant="outline">Default</Badge> : ""}
                </TableCell>
                <TableCell>
                  {t.background_image_base64 ? <Badge className="bg-emerald-500/20 text-emerald-500">Uploaded</Badge> : ""}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline"
                    onClick={() => setEditing({ ...EMPTY_TEMPLATE, ...t })}
                    data-testid={`gv-template-edit-${t.template_id}`}>Edit</Button>
                  <Button size="sm" variant="destructive"
                    onClick={() => remove(t)}
                    disabled={t.is_default}
                    data-testid={`gv-template-delete-${t.template_id}`}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                No templates yet. Click <b>New Template</b> to add one.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {editing && (
        <TemplateEditorDialog
          template={editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Card>
  );
}


function TemplateEditorDialog({ template, token, onClose, onSaved }) {
  const [form, setForm] = useState(template);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onBgUpload = async (file) => {
    if (!file) { set("background_image_base64", null); return; }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Background image must be under 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => set("background_image_base64", e.target.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api("/settings/templates/save", token, {
        template_id: form.template_id || null,
        name: form.name.trim(),
        currency: (form.currency || "*").toUpperCase(),
        amount: Number(form.amount || 0),
        is_default: !!form.is_default,
        bg_color_1: form.bg_color_1,
        bg_color_2: form.bg_color_2,
        bg_color_3: form.bg_color_3,
        accent_color: form.accent_color,
        accent2_color: form.accent2_color,
        ink_color: form.ink_color,
        ink_soft_color: form.ink_soft_color,
        stroke_color: form.stroke_color,
        background_image_base64: form.background_image_base64 || null,
      });
      toast.success("Template saved");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Live preview theme in the same shape PremiumVoucherCard expects.
  const previewTheme = useMemo(() => ({
    label: form.name || "Preview",
    bg: `radial-gradient(circle at 20% 15%,${form.bg_color_1} 0%,${form.bg_color_2} 45%,${form.bg_color_3} 100%)`,
    accent: `linear-gradient(135deg,${form.accent_color} 0%,${form.accent2_color} 55%,${form.accent_color} 100%)`,
    ink: form.ink_color,
    inkSoft: form.ink_soft_color,
    stroke: form.stroke_color,
  }), [form]);

  const previewVoucher = {
    voucher_number: "PBGV-2026-PREVIEW",
    voucher_amount: Number(form.amount) || 500,
    currency: form.currency && form.currency !== "*" ? form.currency : "INR",
    recipient_name: "Prajakta Kulkarni",
    purchaser_name: "Jayanti & Pranav",
    personal_message: "Love you always & forever",
    occasion: "Anniversary",
    issued_at: new Date().toISOString(),
    expires_at: new Date(Date.now() + 365 * 86400_000).toISOString(),
    center: "PB-HSR",
    status: "ACTIVE",
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="gv-template-editor">
        <DialogHeader>
          <DialogTitle>{form.template_id ? "Edit Template" : "New Template"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-2">
          {/* Form */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Template Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="e.g. Royal Burgundy" data-testid="gv-tpl-name" />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="gv-tpl-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="JPY">JPY</SelectItem>
                    <SelectItem value="*">Any (default)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (exact tier)</Label>
                <Input type="number" value={form.amount}
                  onChange={(e) => set("amount", e.target.value)}
                  placeholder="e.g. 1000" data-testid="gv-tpl-amount" />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="tpl-default" checked={!!form.is_default}
                  onChange={(e) => set("is_default", e.target.checked)}
                  data-testid="gv-tpl-is-default" />
                <Label htmlFor="tpl-default" className="cursor-pointer">
                  Use as default (applies when no exact tier matches)
                </Label>
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Colors</div>
              <div className="grid grid-cols-2 gap-2">
                <ColorField label="Background 1" value={form.bg_color_1} onChange={(v) => set("bg_color_1", v)} testid="tpl-bg1" />
                <ColorField label="Background 2" value={form.bg_color_2} onChange={(v) => set("bg_color_2", v)} testid="tpl-bg2" />
                <ColorField label="Background 3" value={form.bg_color_3} onChange={(v) => set("bg_color_3", v)} testid="tpl-bg3" />
                <ColorField label="Accent" value={form.accent_color} onChange={(v) => set("accent_color", v)} testid="tpl-accent" />
                <ColorField label="Accent Highlight" value={form.accent2_color} onChange={(v) => set("accent2_color", v)} testid="tpl-accent2" />
                <ColorField label="Ink (bright text)" value={form.ink_color} onChange={(v) => set("ink_color", v)} testid="tpl-ink" />
                <ColorField label="Ink Soft (labels)" value={form.ink_soft_color} onChange={(v) => set("ink_soft_color", v)} testid="tpl-ink-soft" />
                <ColorField label="Stroke (borders)" value={form.stroke_color} onChange={(v) => set("stroke_color", v)} testid="tpl-stroke" />
              </div>
            </div>

            <div className="pt-2 border-t">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Background Image (optional)
              </div>
              <div className="text-xs text-muted-foreground mb-2">
                Upload an ornate PNG background to replace the color gradient. Max 3 MB, 1600×1100 recommended.
              </div>
              <input type="file" accept="image/png,image/jpeg"
                onChange={(e) => onBgUpload(e.target.files?.[0])}
                data-testid="gv-tpl-bg-upload"
              />
              {form.background_image_base64 && (
                <div className="mt-2 flex items-center gap-2">
                  <img src={form.background_image_base64} alt="bg preview"
                    className="h-16 rounded border border-white/10" />
                  <Button size="sm" variant="outline"
                    onClick={() => set("background_image_base64", null)}
                    data-testid="gv-tpl-bg-clear">Remove</Button>
                </div>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Live Preview</div>
            <PremiumVoucherCard
              v={previewVoucher}
              verifyUrl="https://purnabramha.com/gv/verify?preview=1"
              overrideTheme={previewTheme}
              overrideBgImage={form.background_image_base64}
              showFlipToggle={false}
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}
            data-testid="gv-tpl-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="gv-tpl-save">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Save Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="h-9 w-14 rounded cursor-pointer border border-white/10 bg-transparent"
          data-testid={`${testid}-picker`}
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)}
          className="h-9 font-mono text-xs" placeholder="#a1b2c3"
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}
